import {v4 as uuidv4} from 'uuid'
import {getCookie, getCookieName} from '../common/utils/cookieUtils'

class WebSocketServiceProvider {
  private socket: WebSocket | null = null
  private clientId: string
  private messageHandlers: Map<string, (data: any) => void> = new Map()
  private connectionListeners: Array<(status: 'connected' | 'disconnected') => void> = []
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectTimeout: NodeJS.Timeout | null = null
  private registeredActivationId: string | null = null

  constructor() {
    this.clientId = uuidv4()
  }

  addConnectionListener(listener: (status: 'connected' | 'disconnected') => void) {
    this.connectionListeners.push(listener)
  }

  removeConnectionListener(listener: (status: 'connected' | 'disconnected') => void) {
    this.connectionListeners = this.connectionListeners.filter((l) => l !== listener)
  }

  private notifyConnectionClosed() {
    this.connectionListeners.forEach((listener) => listener('disconnected'))
  }

  private notifyConnectionOpened() {
    this.connectionListeners.forEach((listener) => listener('connected'))
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        resolve()
        return
      }

      const token = getCookie(getCookieName())
      if (!token) {
        reject(new Error('No authentication token found'))
        return
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      let dynamicUrl = window.location.origin === 'http://localhost:3000' ? 'http://localhost:8000' : window.location.origin

      if (dynamicUrl.includes('ai-portal')) {
        dynamicUrl = dynamicUrl.replace('ai-portal', 'ai-prediction')
      }

      const host = new URL(dynamicUrl).host
      const wsUrl = `${protocol}//${host}/ws/copilot/${this.clientId}`

      this.socket = new WebSocket(wsUrl)

      this.socket.onopen = () => {
        console.log('WebSocket connection established')
        this.reconnectAttempts = 0
        this.notifyConnectionOpened()

        if (this.registeredActivationId) {
          this.registerForActivationUpdates(this.registeredActivationId)
        }

        resolve()
      }

      this.socket.onmessage = (event) => {
        console.log('WebSocket message received:', event.data)
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error)
        this.notifyConnectionClosed()
        this.attemptReconnect()
        reject(error)
      }

      this.socket.onclose = () => {
        console.log('WebSocket connection closed')
        this.notifyConnectionClosed()
        this.attemptReconnect()
      }
    })
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)

      console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`)

      this.reconnectTimeout = setTimeout(() => {
        this.connect().catch(() => {
          console.log('Reconnection attempt failed')
        })
      }, delay)
    }
  }

  registerForActivationUpdates(activationId: string): void {
    this.registeredActivationId = activationId

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendMessage(
        JSON.stringify({
          type: 'register_activation',
          activationId: activationId,
        })
      )
    } else {
      console.log('WebSocket not connected, will register when connected')
    }
  }

  registerConversationHandler(conversationId: string, handler: (data: any) => void) {
    console.log(`Registering handler for conversation: ${conversationId}`)
    this.messageHandlers.set(conversationId, handler)

    if (this.socket?.readyState === WebSocket.OPEN && conversationId !== 'xpert_knowledge_updates') {
      this.sendMessage(
        JSON.stringify({
          type: 'register',
          conversationId: conversationId,
        })
      )
    }
  }

  unregisterConversationHandler(conversationId: string) {
    this.messageHandlers.delete(conversationId)
  }

  private handleMessage(data: any) {
    if (data.type === 'registered') {
      console.log(`Successfully registered for conversation: ${data.conversationId}`)
      return
    }

    if (data.type === 'activation_registered') {
      console.log(`Successfully registered for activation: ${data.activationId}`)
      return
    }

    if (data.type && data.type.startsWith('xpert_knowledge_')) {
      const xkHandler = this.messageHandlers.get('xpert_knowledge_updates')
      if (xkHandler) {
        xkHandler(data)
        return
      }
    }

    let conversationId = data.conversationId

    if (conversationId && this.messageHandlers.has(conversationId)) {
      console.log(`Found handler for conversation: ${conversationId}`)
      const handler = this.messageHandlers.get(conversationId)
      if (handler) {
        handler(data)
      }
    } else {
      console.log('No direct match found, checking all handlers')
      for (const [id, handler] of Array.from(this.messageHandlers.entries())) {
        console.log(`Trying handler for: ${id}`)
        handler(data)
      }
    }
  }

  isConnected() {
    return this.socket?.readyState === WebSocket.OPEN
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    this.registeredActivationId = null

    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
  }

  getClientId() {
    return this.clientId
  }

  sendMessage(message: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log('Sending WebSocket message:', message)
      this.socket.send(message)
    } else {
      console.error('WebSocket not connected, cannot send message:', message)
    }
  }

  createCopilotMessage(conversationId: string, content: any, senderType: string = 'HUMAN'): void {
    const token = getCookie(getCookieName())
    if (!token) {
      console.error('No authentication token found')
      return
    }

    this.sendMessage(
      JSON.stringify({
        type: 'create_message',
        conversationId: conversationId,
        content: content,
        senderType: senderType,
        token: token,
      })
    )
  }
}

export const WebSocketService = new WebSocketServiceProvider()
